import React from 'react'
import { api } from '../../function/axios'

/*
 * The source icon set the admin draws — GET /source-icons, fetched once per
 * page load and shared by every <SourceIcon>: the active collection merged
 * with its changes from System → Interface → Source icons. The editor calls
 * reloadSourceIcons() after a save or a switch so the change shows at once.
 */

export type SourceIconSpec = {
  category: string
  /** a ready 24×24 SVG in currentColor */
  svg?: string
  /** a picture (data: URL) */
  logo?: string
  /** up to three letters */
  text?: string
  /** corner mark: an SVG or up to three letters */
  badge?: string
  label?: string
  /** a text badge's background (#rrggbb) */
  color?: string
}
export type SourceIconCategory = { key: string; label: string; hue: number }
/** tile: on a tinted tile; bare: the picture alone (the original icons); text: letters on a solid colour (the old result badges) */
export type SourceIconStyle = 'tile' | 'bare' | 'text'
export type SourceIconSet = { version: string; collection: string; style: SourceIconStyle; categories: SourceIconCategory[]; icons: Record<string, SourceIconSpec> }

let current: SourceIconSet | null = null
let loading: Promise<void> | null = null
const listeners = new Set<() => void>()

function load(): Promise<void> {
  loading = api({ method: 'GET', url: '/source-icons' })
    .then(({ data }: any) => {
      if (data && data.icons) {
        current = data
        listeners.forEach((fn) => fn())
      }
    })
    .catch(() => { /* no icons — <SourceIcon> draws letters */ })
    .finally(() => { loading = null })
  return loading
}

export const reloadSourceIcons = () => load()

export function useSourceIcons(): SourceIconSet | null {
  const [, tick] = React.useReducer((n: number) => n + 1, 0)
  React.useEffect(() => {
    listeners.add(tick)
    if (!current && !loading) void load()
    return () => { listeners.delete(tick) }
  }, [])
  return current
}

export const categoryHue = (set: SourceIconSet | null, key?: string) =>
  set?.categories.find((c) => c.key === key)?.hue ?? 215
